import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { ModalState, IngredientCategoryForm, IngredientCategory } from '../types/system-parameters.types';

interface IngredientCategoryModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: IngredientCategoryForm, isEdit?: boolean, itemId?: string) => Promise<void>;
  totalCategories?: number;
}

export const IngredientCategoryModal: React.FC<IngredientCategoryModalProps> = ({
  modalState,
  onClose,
  onSave,
  totalCategories = 0
}) => {
  const [formData, setFormData] = useState<IngredientCategoryForm>({
    name: '',
    description: '',
    displayOrder: 1
  });
  const [loading, setLoading] = useState(false);

  // Resetear formulario cuando se abra en modo crear o cuando cambie el item
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const category = modalState.item as IngredientCategory;
        setFormData({
          name: category.additionalData.name,
          description: category.additionalData.description,
          displayOrder: category.additionalData.displayOrder
        });
      } else {
        setFormData({
          name: '',
          description: '',
          displayOrder: totalCategories + 1
        });
      }
    }
  }, [modalState.isOpen, modalState.mode, modalState.item, totalCategories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(
        formData,
        modalState.mode === 'edit',
        modalState.item?.id
      );
      onClose();
    } catch (error) {
      console.error('Error al guardar categoría de insumo:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {modalState.mode === 'edit' ? 'Editar Categoría de Insumo' : 'Nueva Categoría de Insumo'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: Carnes"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripcion</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descripcion de la categoría de insumo"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayOrder">Orden de visualizacion</Label>
            <Input
              id="displayOrder"
              type="number"
              min={1}
              value={formData.displayOrder}
              onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.name.trim() || loading}
              className="bg-macartur-primary"
            >
              {loading ? 'Guardando...' : modalState.mode === 'edit' ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
